// externals
import type { Reducer } from 'redux';

// libraries
import type { VehicleBffData } from '@makemydeal/dr-dash-bff-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import { initActionTypes } from '@makemydeal/dr-shared-store';

// consts/enums
import {
    UPDATE_VEHICLE_INFO,
    SHOW_EDIT_VEHICLE,
    HIDE_EDIT_VEHICLE,
    UPDATE_CVI_PRICING,
    DISMISS_CASH_FALLBACK_NOTICE
} from '../actionTypes/vehicleActionTypes';
import { MERGE_FUEL_ECONOMY_FROM_API } from '../actionTypes/fuelEconomyActionTypes';
import { PRODUCTS_REQUEST_OVERRIDDEN } from '../actionTypes/vehicleProtectionActionTypes';

// utils
import { REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR, VEHICLE_CHANGE_SUCCESS } from '@makemydeal/dr-activities-change-vehicle';
import { UPDATE_VEHICLE_WITH_SELECTED_RECOMMENDATION } from '../actionTypes/deskingActionTypes';
import type { FuelEconomyApiVehicle, MergeFuelEconomyFromApiPayload } from '../types/fuelEconomyTypes';
import { normalizeVin } from '../utils/vinUtils';

const { INIT_PENCIL_SUCCESS, INIT_PENCIL_INVALID_DEAL } = initActionTypes;

// TODO: DASH - move this to reducer (and resolve dr-activities-common type use - it shouldn't be duplicated)
export interface IVehicleState extends VehicleBffData {
    initialRetailPrice?: number;
    // from CVI
    inventoryDate?: string;
    notes?: string;
    location?: {
        isOnLot: boolean;
        gps?: string;
        lot?: string;
    };
    showVehicleChangeSnackBar?: boolean;
    cashFallbackFromOfferType?: OfferType;
    showEditVehicle?: boolean;
    cviCost?: number;
    dealCostAtCviFetch?: number;
    cviInvoiceAmount?: number;
    dealInvoiceAtCviFetch?: number;
}

export type VehicleReducer = Reducer<IVehicleState>;

// The CVI pricing snapshot describes the vehicle it was fetched for, so it is dropped whenever the
// vehicle is replaced; `hasCviCostMismatch` and `hasCviInvoiceMismatch` compare the current values against it.
const clearedCviPricing = {
    cviCost: undefined,
    dealCostAtCviFetch: undefined,
    cviInvoiceAmount: undefined,
    dealInvoiceAtCviFetch: undefined
};

export const reducer: VehicleReducer = (state: IVehicleState = {}, action: any) => {
    switch (action.type) {
        case INIT_PENCIL_INVALID_DEAL:
        case INIT_PENCIL_SUCCESS: {
            const vehicleData = {
                ...state,
                ...action.payload.vehicle,
                retailPrice: action.payload.vehicle.retailPrice || action.payload.vehicle.listedPrice,
                initialRetailPrice: action.payload.vehicle.retailPrice,
                cashFallbackFromOfferType: undefined
            };
            if (vehicleData.fuel === 'Electric' && !vehicleData.cylinders) {
                vehicleData.cylinders = 0;
            }
            if (vehicleData.wholesaleValueType === 'Loan') {
                vehicleData.wholesaleValueType = 'FPP';
            }

            return vehicleData;
        }
        case UPDATE_VEHICLE_INFO: {
            return {
                ...state,
                ...action.payload
            };
        }

        case MERGE_FUEL_ECONOMY_FROM_API: {
            const payload = action.payload as MergeFuelEconomyFromApiPayload | undefined;
            const sellingVehicles = Array.isArray(payload?.sellingVehicles) ? payload.sellingVehicles : [];
            const stateVin = normalizeVin(state.vin);
            const mpg = sellingVehicles.find(
                (vehicle: FuelEconomyApiVehicle) =>
                    vehicle.ok && normalizeVin(vehicle.vin) === stateVin && vehicle.fuelEconomy != null
            )?.fuelEconomy;
            if (!mpg) return state;

            return {
                ...state,
                ...(mpg.city != null && { cityFuelEconomy: mpg.city }),
                ...(mpg.highway != null && { highwayFuelEconomy: mpg.highway }),
                ...(mpg.combined != null && { fuelEconomy: mpg.combined, combinedFuelEconomy: mpg.combined })
            };
        }

        case VEHICLE_CHANGE_SUCCESS: {
            const vehicleData = {
                ...state,
                ...action.payload,
                cashFallbackFromOfferType: action.payload?.cashFallbackFromOfferType,
                showVehicleChangeSnackBar: true,
                ...clearedCviPricing
            };
            if (vehicleData.fuel === 'Electric' && !vehicleData.cylinders) {
                vehicleData.cylinders = 0;
            }
            return vehicleData;
        }

        case offerReduxActionTypes.UPDATED_OFFER_TYPE:
        case DISMISS_CASH_FALLBACK_NOTICE: {
            return state.cashFallbackFromOfferType ? { ...state, cashFallbackFromOfferType: undefined } : state;
        }

        case REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR: {
            return {
                ...state,
                showVehicleChangeSnackBar: false
            };
        }

        case HIDE_EDIT_VEHICLE: {
            return { ...state, showEditVehicle: false };
        }

        case SHOW_EDIT_VEHICLE: {
            return { ...state, showEditVehicle: true };
        }

        case UPDATE_CVI_PRICING: {
            return {
                ...state,
                cviCost: action.payload.cviCost,
                dealCostAtCviFetch: action.payload.dealCostAtCviFetch,
                cviInvoiceAmount: action.payload.cviInvoiceAmount,
                dealInvoiceAtCviFetch: action.payload.dealInvoiceAtCviFetch
            };
        }

        // The vehicle owns `vehicleUse`, and `getVppSurchargesOverrides` derives the VPP `usage` attribute from
        // it, so the VPP drawer's edit has to land here or it would be read straight back out.
        case PRODUCTS_REQUEST_OVERRIDDEN: {
            const { usage } = action.payload ?? {};
            return usage === state.vehicleUse ? state : { ...state, vehicleUse: usage };
        }

        case offerReduxActionTypes.VEHICLE_RETAIL_PRICE_CHANGED: {
            return {
                ...state,
                retailPrice: action.payload.retailPrice
            };
        }
        case offerReduxActionTypes.UPDATED_OFFER_PRICE_IMMEDIATE: {
            return {
                ...state,
                listedPrice: action.payload
            };
        }
        case offerReduxActionTypes.SWITCH_VEHICLE:
            return { ...action.payload };
        case offerReduxActionTypes.UPDATED_VEHICLE_INFO_DETAILS:
            return action.payload?.vehicle !== undefined ? { ...state, ...action.payload.vehicle } : state;

        case UPDATE_VEHICLE_WITH_SELECTED_RECOMMENDATION: {
            const selectedVehicle = action.payload;

            return {
                ...state,
                ...selectedVehicle,
                mileage: selectedVehicle.odometer,
                initialRetailPrice: selectedVehicle.retailPrice,
                ...clearedCviPricing
            };
        }

        default: {
            return state;
        }
    }
};
